import { useState, useCallback, useEffect } from 'react';
import type {
  OutlinedDropdownOption,
  UseOutlinedDropdownProps,
  UseOutlinedDropdownReturn,
} from './types';

export const useOutlinedDropdown = ({
  options,
  onChange,
  disabled = false,
}: UseOutlinedDropdownProps): UseOutlinedDropdownReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalOptions, setInternalOptions] =
    useState<OutlinedDropdownOption[]>(options);

  useEffect(() => {
    setInternalOptions(options);
  }, [options]);

  const isAllChecked =
    internalOptions.length > 0 && internalOptions.every((o) => o.checked);
  const isIndeterminate =
    !isAllChecked && internalOptions.some((o) => o.checked);

  const toggleOpen = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const toggleAll = useCallback(() => {
    if (disabled) {
      return;
    }
    const next = internalOptions.map((o) =>
      o.disabled ? o : { ...o, checked: !isAllChecked }
    );
    setInternalOptions(next);
    onChange?.(next);
  }, [disabled, internalOptions, isAllChecked, onChange]);

  const toggleOption = useCallback(
    (value: string) => {
      if (disabled) {
        return;
      }
      const next = internalOptions.map((o) =>
        o.value === value && !o.disabled ? { ...o, checked: !o.checked } : o
      );
      setInternalOptions(next);
      onChange?.(next);
    },
    [disabled, internalOptions, onChange]
  );

  return {
    isOpen,
    internalOptions,
    isAllChecked,
    isIndeterminate,
    toggleOpen,
    toggleAll,
    toggleOption,
  };
};
